// frontend/src/components/mobile/MobileQuickActionButton.jsx
// Project:      Vision Systems / Vision ANPR
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-09-23
// Updated:      2026-09-23 (React 19 default-value hotfix)
//
// Description:  Accessible presentation component for one mobile quick-action
//               node. Supports Font Awesome definitions, React icon components,
//               or pre-rendered icon elements without coupling the navigation
//               shell to one icon source.

import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

function ActionIcon({ icon }) {
  if (React.isValidElement(icon)) {
    return React.cloneElement(icon, {
      'aria-hidden': true,
      focusable: false,
    });
  }

  if (typeof icon === 'function') {
    const IconComponent = icon;
    return <IconComponent aria-hidden="true" focusable="false" />;
  }

  return <FontAwesomeIcon icon={icon} aria-hidden="true" fixedWidth />;
}

ActionIcon.propTypes = {
  icon: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.func,
    PropTypes.element,
    PropTypes.array,
  ]).isRequired,
};

export default function MobileQuickActionButton({
  action,
  buttonRef = null,
  isFront = false,
  isHovered = false,
  isInteractive = false,
  onBlur,
  onFocus,
  onPointerEnter,
  onPointerLeave,
  onSelect,
  style,
}) {
  const handleClick = (event) => {
    event.stopPropagation();
    onSelect(action, event);
  };

  return (
    <div
      className={`vision-mobile-quick-action${isFront ? ' is-front' : ''}${
        isHovered ? ' is-hovered' : ''
      }`}
      style={style}
      aria-hidden={!isInteractive}
    >
      <span
        className="vision-mobile-quick-action__label"
        aria-hidden="true"
      >
        {action.label}
      </span>

      <button
        ref={buttonRef}
        type="button"
        className="vision-mobile-quick-action__button"
        onBlur={onBlur}
        onClick={handleClick}
        onFocus={onFocus}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
        tabIndex={isInteractive ? 0 : -1}
        aria-label={action.label}
        disabled={action.disabled === true}
        data-action-id={action.id}
      >
        <ActionIcon icon={action.icon} />
      </button>
    </div>
  );
}

MobileQuickActionButton.propTypes = {
  action: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.func,
      PropTypes.element,
      PropTypes.array,
    ]).isRequired,
    disabled: PropTypes.bool,
  }).isRequired,
  buttonRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  isFront: PropTypes.bool,
  isHovered: PropTypes.bool,
  isInteractive: PropTypes.bool,
  onBlur: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
  onPointerEnter: PropTypes.func.isRequired,
  onPointerLeave: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  style: PropTypes.object.isRequired,
};
